import { AxiosError } from 'axios';
import useDebounce from 'hooks/useDebounce';
import { useBranchContext } from 'providers/BranchProvider';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState
} from 'react';
import { Col, Form, ListGroup, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInterceptors';
import { UnitOfMeasure } from 'utils/enums';
import { toastConfig } from 'utils/toastConfig';
import {
  TProcurementInvoiceDetails,
  TProcurementItemsDetails,
  TProcurementReturns,
  TProductDetails,
  TSupplierDetails
} from 'utils/types';
import * as Yup from 'yup';

type ProductOrProcurementItem =
  | (TProcurementItemsDetails & { type: 'procurement' })
  | (TProductDetails & { type: 'product' });

type CreateReturnDetailsFormProps = {
  suppliers?: TSupplierDetails[];
};

export type CreateReturnDetailsFormHandle = {
  submit: () => Promise<boolean | undefined>;
};

const CreateReturnDetailsForm = forwardRef<
  CreateReturnDetailsFormHandle,
  CreateReturnDetailsFormProps
>(({ suppliers }, ref) => {
  const [returnDetails, setReturnDetails] = useState({
    productId: null,
    procurementInvoiceId: null,
    supplierId: null,
    quantity: null,
    unitOfMeasure: null,
    unitPrice: null,
    returnReason: ''
  });
  const [invoice, setInvoice] = useState<TProcurementInvoiceDetails[]>([]);
  const [products, setProducts] = useState<ProductOrProcurementItem[]>([]);
  const [maxQuantity, setMaxQuantity] = useState<number | undefined>(undefined);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [showInvoiceDropdown, setShowInvoiceDropdown] =
    useState<boolean>(false);
  const [showProductDropdown, setShowProductDropdown] =
    useState<boolean>(false);
  const [productSearchTerm, setProductSearchTerm] = useState<string>('');
  const [invoiceIdList, setInvoiceIdList] = useState<(number | undefined)[]>(
    []
  );
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const { selectedBranch } = useBranchContext();

  const fetchInvoice = useCallback(
    async (searchTerm: string = '') => {
      const queryParams = searchTerm
        ? `page=1&pageSize=10&search=${searchTerm}&orderBy=deliveredOn&orderByDesc=true&status=Processed`
        : `page=1&pageSize=10&orderBy=deliveredOn&orderByDesc=true&status=Processed`;

      try {
        const res = await axiosInstance.get(
          `/api/v1/${selectedBranch?.branchDomain}/procurementInvoices?${queryParams}`
        );
        setInvoice(res.data.data);
      } catch (error) {
        console.error(error);
      }
    },
    [selectedBranch?.branchDomain]
  );

  const fetchProducts = async (product: string = '') => {
    const { procurementInvoiceId } = returnDetails;
    const selectedInvoice = invoiceIdList.some(
      id => id === procurementInvoiceId
    );

    const url = selectedInvoice
      ? `/api/v1/${selectedBranch?.branchDomain}/procurementInvoices/${procurementInvoiceId}/items?page=1&pageSize=100${product ? `&search=${product}` : ''}`
      : `/api/v1/${selectedBranch?.branchDomain}/products?page=1&pageSize=10&search=${product}`;

    try {
      const { data } = await axiosInstance.get(url);

      const mappedData = data.data.map(
        (item: TProcurementItemsDetails | TProductDetails) => ({
          ...item,
          type: selectedInvoice ? 'procurement' : 'product'
        })
      );

      setProducts(mappedData);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const fetchSupplierDetails = async (invoiceId: number) => {
    try {
      const res = await axiosInstance.get(
        `/api/v1/${selectedBranch?.branchDomain}/procurementInvoices/${invoiceId}`
      );
      return res.data.supplierId;
    } catch (error) {
      if (error instanceof AxiosError) {
        console.error(error.response?.data.errorMessage);
      }
    }
  };

  const handleFormChange = <K extends keyof TProcurementReturns>(
    field: K,
    value: TProcurementReturns[K]
  ) => {
    setReturnDetails(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleProductChange = (productId: number) => {
    const selectedProductId = productId;

    const selectedProduct = products.find(
      item => item.productId === selectedProductId
    );

    if (selectedProduct) {
      handleFormChange('productId', selectedProduct.productId);

      if (selectedProduct.type === 'procurement') {
        handleFormChange('quantity', selectedProduct.quantity);
        handleFormChange('unitPrice', selectedProduct.procurementPrice);
        setProductSearchTerm(selectedProduct.productName ?? '');
        setMaxQuantity(selectedProduct.quantity);
      } else if (selectedProduct.type === 'product') {
        handleFormChange('unitPrice', selectedProduct.currentSellingPrice || 0);
        setProductSearchTerm(selectedProduct.name ?? '');
        setMaxQuantity(undefined);
      }
      setShowProductDropdown(false);
    }
  };

  const handleSelectInvoice = (invoiceId: number) => {
    handleFormChange('procurementInvoiceId', invoiceId);
    setSearchTerm(invoiceId.toString());
    setShowInvoiceDropdown(false);
  };

  const handleInvoiceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchTerm(value);
    handleFormChange('procurementInvoiceId', +value);
    setShowInvoiceDropdown(true);
  };

  const handleSearchProduct = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setProductSearchTerm(value);
    handleFormChange('productId', +value);
    setShowProductDropdown(true);
  };

  const handleBarcodeChange = async (barcode: string) => {
    try {
      const res = await axiosInstance.get(
        `/api/v1/${selectedBranch?.branchDomain}/products/byBarcode?barcode=${barcode}`
      );
      const { data } = res;
      handleFormChange('procurementInvoiceId', data.procurementInvoiceId);
      setSearchTerm(data.procurementInvoiceId);
      const supplierId = await fetchSupplierDetails(data.procurementInvoiceId);
      handleFormChange('supplierId', supplierId);
      handleFormChange('productId', data.productId);
      setProductSearchTerm(data.name);
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const procurementReturnSchema = Yup.object().shape({
    productId: Yup.number().required('Product name is required'),
    supplierId: Yup.number().required('Supplier name is required'),
    quantity: Yup.number()
      .required('Quantity is required')
      .min(1, 'At least one item must be returned'),
    unitOfMeasure: Yup.number().required('Unit of measure required'),
    unitPrice: Yup.number().required('Unit price is required'),
    returnReason: Yup.string().required('Reason is required')
  });

  const handleSubmit = async () => {
    try {
      await procurementReturnSchema.validate(returnDetails, {
        abortEarly: false
      });
      await axiosInstance.post(
        `/api/v1/${selectedBranch?.branchDomain}/procurementReturns`,
        returnDetails
      );
      toast.success('Procurement return submitted', {
        autoClose: 2000,
        ...toastConfig
      });
      return true;
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        error.inner.forEach(err => {
          toast.error(err.message, { ...toastConfig, autoClose: 3000 });
        });
      } else if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          position: 'top-right',
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined
        });
      }
      return false;
    }
  };

  useEffect(() => {
    if (debouncedSearchTerm) {
      fetchInvoice(debouncedSearchTerm);
    } else {
      fetchInvoice();
    }
  }, [debouncedSearchTerm, fetchInvoice]);

  const debouncedProductSearchTerm = useDebounce(productSearchTerm, 500);

  useEffect(() => {
    if (debouncedProductSearchTerm) {
      fetchProducts(debouncedProductSearchTerm);
    } else {
      fetchProducts();
    }
  }, [debouncedProductSearchTerm]);

  useEffect(() => {
    fetchProducts();
  }, [debouncedSearchTerm]);

  useEffect(() => {
    if (returnDetails.productId && products.length > 0) {
      handleProductChange(returnDetails.productId);
    }
  }, [returnDetails.productId, products]);

  useEffect(() => {
    if (invoice.length > 0) {
      const idList = invoice.map(i => i.procurementInvoiceId);
      setInvoiceIdList(idList);
    }
  }, [invoice]);

  useImperativeHandle(ref, () => ({
    submit: handleSubmit
  }));

  return (
    <>
      <Form>
        <Row className="mb-3">
          <Form.Group as={Col} controlId="supplierId">
            <Form.Label>Scan Barcode (optional)</Form.Label>
            <Form.Control
              placeholder="Barcode"
              onBlur={e => handleBarcodeChange(e.target.value)}
            ></Form.Control>
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col} controlId="supplierId">
            <Form.Label>Supplier Name</Form.Label>
            <Form.Select
              value={returnDetails.supplierId || ''}
              onChange={e => handleFormChange('supplierId', +e.target.value)}
            >
              <option> All</option>
              {suppliers?.map(supplier => (
                <option key={supplier.supplierId} value={supplier.supplierId}>
                  {supplier.name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col} controlId="procurementInvoiceId">
            <Form.Label>Procurement Invoice ID</Form.Label>
            <Form.Control
              type="text"
              value={searchTerm}
              placeholder="Search invoice number or Select invoice"
              onChange={handleInvoiceChange}
              onFocus={() => setShowInvoiceDropdown(true)}
              onBlur={() =>
                setTimeout(() => setShowInvoiceDropdown(false), 200)
              }
            />
            <ListGroup>
              {showInvoiceDropdown && invoice.length > 0 && (
                <div
                  style={{
                    overflowY: 'scroll',
                    maxHeight: '10rem',
                    borderBottom: '1px solid black',
                    width: '100%'
                  }}
                >
                  {invoice.map((i, index) => (
                    <ListGroup.Item
                      key={index}
                      variant="secondary"
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        i.procurementInvoiceId !== undefined &&
                        handleSelectInvoice(i.procurementInvoiceId)
                      }
                    >
                      ({i.invoiceNumber}) {i.supplierName}
                    </ListGroup.Item>
                  ))}
                </div>
              )}
            </ListGroup>
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group
            as={Col}
            controlId="productId"
            className="position-relative"
          >
            <Form.Label>Product Name</Form.Label>
            <Form.Control
              type="text"
              value={productSearchTerm}
              placeholder="Search or Select product"
              onChange={handleSearchProduct}
              onFocus={() => setShowProductDropdown(true)}
              onBlur={() =>
                setTimeout(() => setShowProductDropdown(false), 200)
              }
            />
            <ListGroup>
              {showProductDropdown && products.length > 0 && (
                <div
                  style={{
                    overflowY: 'scroll',
                    maxHeight: '10rem',
                    borderBottom: '1px solid black',
                    backgroundColor: '#141824',
                    width: '100%'
                  }}
                >
                  {products.map((i, index) => (
                    <ListGroup.Item
                      key={index}
                      variant="secondary"
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (i.productId == null) return;
                        handleProductChange(i.productId);
                      }}
                    >
                      {i.type === 'procurement' ? i.productName : i.name} (
                      {i.productId})
                    </ListGroup.Item>
                  ))}
                </div>
              )}
            </ListGroup>
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col} controlId="quantity">
            <Form.Label>Quantity</Form.Label>
            <Form.Control
              type="number"
              placeholder="Quantity"
              max={maxQuantity ?? undefined}
              value={returnDetails.quantity || ''}
              onChange={e => handleFormChange('quantity', +e.target.value)}
            />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col} controlId="unitOfMeasure">
            <Form.Label>Unit of Measure</Form.Label>
            <Form.Select
              value={returnDetails.unitOfMeasure || ''}
              onChange={e => handleFormChange('unitOfMeasure', +e.target.value)}
            >
              <option> All</option>
              <option value={UnitOfMeasure.Piece}>Piece</option>
              <option value={UnitOfMeasure.Set}>Set</option>
              <option value={UnitOfMeasure.Meter}>Meter</option>
              <option value={UnitOfMeasure.Foot}>Foot</option>
              <option value={UnitOfMeasure.Inches}>Inches</option>
            </Form.Select>
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col} controlId="unitPrice">
            <Form.Label>Unit Price</Form.Label>
            <Form.Control
              type="text"
              placeholder="Unit price"
              value={returnDetails.unitPrice || ''}
              onChange={e => handleFormChange('unitPrice', e.target.value)}
            />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col} controlId="returnReason">
            <Form.Label>Return Reason</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              placeholder="Reason"
              value={returnDetails.returnReason || ''}
              onChange={e => handleFormChange('returnReason', e.target.value)}
            />
          </Form.Group>
        </Row>
      </Form>
    </>
  );
});

export default CreateReturnDetailsForm;
